// Konva overlay sitting on top of one PDF page's <canvas>.
//
// Layout model (per page):
//   - Inline glyphs (underlines, boxes, icons) render at the annotation's
//     natural (x, y) position on the page.
//   - Margin texts for language + note annotations are stacked vertically in
//     the right margin to prevent overlap. If an entry would be pushed more
//     than 4 line-heights below its natural y it overflows to a second column
//     at rightMarginPx further right.
//   - Content annotations show only a superscript number in the margin; full
//     text renders in a footnote area appended below the page inside the same
//     Konva Stage (so PDF export captures it).
//   - Point deductions are appended to every margin text and footnote.

import { useEffect, useMemo, useRef } from "react";
import type Konva from "konva";
import { Layer, Rect, Stage, Text } from "react-konva";

import type { Annotation, AnnotationStatus, AnnotationType } from "../api/types";
import { usePaperTranscript, useSettings } from "../api/hooks";
import {
  registerOverlayStage,
  unregisterOverlayStage,
} from "../export/exportPaper";
import { useAnnotationStore } from "../state/annotationStore";
import { useDevStore } from "../state/devStore";

const HIDDEN_STATUSES: AnnotationStatus[] = ["invalidated"];
const BASE_CAVEAT_PX = 14;
const ACCENT = "#F56C73";
const ACCENT_SEL = "#c84049";
const OCR_WARN = "#FADC53";
const MUTED = "rgba(245, 108, 115, 0.45)";
const WORD_SEL = "#22c55e";

// ── Public interfaces ─────────────────────────────────────────────────────

export interface WordDoubleClickInfo {
  pageNumber: number;
  xNorm: number;
  yNorm: number;
  widthNorm: number;
  heightNorm: number;
  screenX: number;
  screenY: number;
}

export interface AnnotationOverlayProps {
  paperId: number;
  pageNumber: number;
  widthPx: number;
  heightPx: number;
  zoom: number;
  onWordDoubleClick?: (info: WordDoubleClickInfo) => void;
  visibleTypes?: Set<AnnotationType>;
  /** Called whenever the footnote area height changes so ScanCanvas can add matching paddingBottom. */
  onFootnoteHeight?: (height: number) => void;
}

// ── Margin layout ─────────────────────────────────────────────────────────

interface MarginSlot { y: number; col: 0 | 1 }
interface FootnoteEntry { id: number; num: number; text: string; deduction: number }

interface Layout {
  marginSlots: Map<number, MarginSlot>;
  footnoteNums: Map<number, number>;
  footnotes: FootnoteEntry[];
}

/** Height of the footnote area given the actual rendered column width.
 *  Shared by paddingBottom estimate and the renderer's yOff advance. */
function computeFootnoteHeight(
  footnotes: FootnoteEntry[],
  colWidth: number,
  fontPx: number,
): number {
  if (footnotes.length === 0) return 0;
  const fnFontPx = fontPx * 0.9;
  // Caveat glyphs are ~0.58× fontSize wide on average.
  const cpl = Math.max(10, Math.floor(colWidth / (fnFontPx * 0.58)));
  let h = 14; // gap after the divider line
  for (const fn of footnotes) {
    const text = `[${fn.num}] ${fn.text}${ptSuffix(fn.deduction)}`;
    h += Math.max(1, Math.ceil(text.length / cpl)) * fnFontPx * 1.5 + 5;
  }
  return h;
}

function computeLayout(
  annotations: Annotation[],
  heightPx: number,
  fontPx: number,
): Layout {
  // Slot heights per entry type.
  // Content [n] markers are short; language/note corrections may wrap to ~2 lines.
  const MARKER_H = Math.ceil(fontPx * 1.8);  // height of a single [n] marker
  const TEXT_H   = Math.ceil(fontPx * 3.2);  // height for a correction that may wrap
  const OVERFLOW = 4 * TEXT_H;

  // Content → footnotes (ordered by y so numbering matches reading order).
  const contentAnns = [...annotations]
    .filter((a) => a.type === "content")
    .sort((a, b) => a.y_norm - b.y_norm);

  const footnotes: FootnoteEntry[] = [];
  const footnoteNums = new Map<number, number>();
  let num = 0;
  for (const a of contentAnns) {
    if (a.status !== "accepted" && a.status !== "corrected") continue;
    const text = (a.accepted_text ?? a.content_note ?? "").trim();
    if (!text) continue;
    num++;
    footnotes.push({ id: a.id, num, text, deduction: a.point_deduction });
    footnoteNums.set(a.id, num);
  }

  // All annotations with margin content sorted by y:
  //   - language/note resolved → correction text
  //   - content with footnote → [n] marker
  const marginCandidates = [...annotations]
    .filter((a) => {
      if (a.type === "language" || a.type === "note") return marginText(a) !== "";
      if (a.type === "content") return footnoteNums.has(a.id);
      return false;
    })
    .sort((a, b) => a.y_norm - b.y_norm);

  const marginSlots = new Map<number, MarginSlot>();
  let lastY1 = -Infinity;
  let lastH1 = 0;
  const overflow: Annotation[] = [];

  for (const a of marginCandidates) {
    const h = a.type === "content" ? MARKER_H : TEXT_H;
    const preferred = a.y_norm * heightPx;
    const placed = Math.max(preferred, lastY1 + lastH1);
    if (placed - preferred > OVERFLOW) {
      overflow.push(a);
    } else {
      marginSlots.set(a.id, { y: placed, col: 0 });
      lastY1 = placed;
      lastH1 = h;
    }
  }

  let lastY2 = -Infinity;
  let lastH2 = 0;
  for (const a of overflow) {
    const h = a.type === "content" ? MARKER_H : TEXT_H;
    const preferred = a.y_norm * heightPx;
    const placed = Math.max(preferred, lastY2 + lastH2);
    marginSlots.set(a.id, { y: placed, col: 1 });
    lastY2 = placed;
    lastH2 = h;
  }

  return { marginSlots, footnoteNums, footnotes };
}

// Text that goes into the right margin — only for resolved (accepted/corrected) annotations.
function marginText(a: Annotation): string {
  if (a.status !== "accepted" && a.status !== "corrected") return "";
  if (a.type === "language") return (a.accepted_text ?? a.suggested_text ?? "").trim();
  if (a.type === "note")     return (a.accepted_text ?? a.content_note  ?? "").trim();
  return "";
}

function ptSuffix(deduction: number): string {
  if (deduction === 0) return "";
  const v = Number.isInteger(deduction) ? deduction : deduction.toFixed(1);
  return ` −${v}pt`;
}

// ── Main component ────────────────────────────────────────────────────────

export function AnnotationOverlay({
  paperId,
  pageNumber,
  widthPx,
  heightPx,
  zoom,
  onWordDoubleClick,
  visibleTypes,
  onFootnoteHeight,
}: AnnotationOverlayProps) {
  const stageRef = useRef<Konva.Stage>(null);

  useEffect(() => {
    if (!stageRef.current) return;
    // Register the page-region size (widthPx × heightPx) too: the Stage is wider
    // and/or taller than the page (right margin + footnote area), and the export
    // backend needs the page fraction to size the extended mediabox so the page
    // region maps 1:1 onto the original scan. See export/exportPaper.ts.
    registerOverlayStage(paperId, pageNumber, stageRef.current, widthPx, heightPx);
    return () => unregisterOverlayStage(paperId, pageNumber);
  }, [paperId, pageNumber, widthPx, heightPx]);

  const annotations = useAnnotationStore((s) =>
    s.annotations.filter(
      (a) =>
        a.page_number === pageNumber &&
        !HIDDEN_STATUSES.includes(a.status) &&
        // A group-highlighted flag (raster expansion) shows even if its card
        // type is filtered out — e.g. content highlights in the Raster view.
        (visibleTypes == null || visibleTypes.has(a.type) || s.highlightIds.has(a.id)),
    ),
  );
  const highlightIds = useAnnotationStore((s) => s.highlightIds);
  const frameRegion  = useAnnotationStore((s) => s.frameRegion);
  const selectedId   = useAnnotationStore((s) => s.selectedId);
  const selectedWordKey = useAnnotationStore((s) => s.selectedWordKey);
  const showOcrConfidence = useDevStore((s) => s.showOcrConfidence);
  const { data: settings } = useSettings();
  const ocrThreshold = settings?.ocr_confidence_threshold ?? 0.5;
  const select    = useAnnotationStore((s) => s.select);
  const selectWord = useAnnotationStore((s) => s.selectWord);

  const { data: transcriptData } = usePaperTranscript(paperId);
  const pageWords = useMemo(
    () => transcriptData?.pages.find((p) => p.page_number === pageNumber)?.words ?? [],
    [transcriptData, pageNumber],
  );

  const wordAnnotationMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of annotations) {
      if (a.status !== "pending") continue;
      const key = `${a.page_number}|${a.x_norm.toFixed(3)}|${a.y_norm.toFixed(3)}`;
      if (!map.has(key) || a.type === "ocr_flag") map.set(key, a.id);
    }
    return map;
  }, [annotations]);

  const fontPx = clamp(BASE_CAVEAT_PX * zoom, 11, 22);
  // Right margin sized to exactly 80pt in CSS pixels so annotation positions map
  // correctly when PyMuPDF stamps the overlay over the 80pt-extended mediabox.
  // react-pdf's CSS viewport is pageWidthPt × zoom (1 PDF point = `zoom` CSS px),
  // so 80pt = 80 × zoom CSS px. (No 96-dpi / 4-3 factor: getViewport({scale})
  // already yields point-sized layout pixels; devicePixelRatio only affects the
  // backing canvas resolution, not offsetWidth.)
  const rightMarginPx = Math.round(80 * zoom);

  const { marginSlots, footnoteNums, footnotes } = useMemo(
    () => computeLayout(annotations, heightPx, fontPx),
    [annotations, heightPx, fontPx],
  );

  // Footnote area height computed with the actual column width (widthPx + rightMarginPx - 8).
  // Must be a separate memo so it re-runs when widthPx changes (e.g. zoom).
  const fnColWidth = Math.max(60, widthPx + rightMarginPx - 8);
  const footnoteAreaHeight = useMemo(
    () => computeFootnoteHeight(footnotes, fnColWidth, fontPx),
    [footnotes, fnColWidth, fontPx],
  );

  // Notify parent so it can add matching paddingBottom to the page div.
  useEffect(() => {
    onFootnoteHeight?.(footnoteAreaHeight);
  }, [footnoteAreaHeight, onFootnoteHeight]);

  // All hooks above — safe to return early now.
  if (widthPx === 0 || heightPx === 0) return null;

  const hasCol1   = [...marginSlots.values()].some((s) => s.col === 1);
  const extraRight = rightMarginPx * (hasCol1 ? 2 : 1);
  const stageWidth = widthPx + extraRight;
  const stageHeight = heightPx + (footnoteAreaHeight > 0 ? footnoteAreaHeight + 8 : 0);

  const col0X    = widthPx + 6;
  const col1X    = widthPx + rightMarginPx + 6;
  const colWidth = rightMarginPx - 10;

  return (
    <div style={{ position: "absolute", top: 0, left: 0, overflow: "visible" }}>
      <Stage ref={stageRef} width={stageWidth} height={stageHeight}>
        <Layer>
          {/* Right margin background */}
          <Rect
            x={widthPx} y={0}
            width={extraRight} height={stageHeight}
            fill="#ffffff" listening={false}
          />

          {/* Word hit-rects */}
          {pageWords.map((w, i) => {
            const wx = w.x_norm * widthPx;
            const wy = w.y_norm * heightPx;
            const ww = Math.max(w.width_norm * widthPx, 4);
            const wh = Math.max(w.height_norm * heightPx, 4);
            const wordKey = `${pageNumber}|${w.x_norm.toFixed(3)}|${w.y_norm.toFixed(3)}`;
            const annotId = wordAnnotationMap.get(wordKey);
            const isWordSelected = selectedWordKey === wordKey && annotId == null;
            return (
              <WordHitRect
                key={i}
                x={wx} y={wy} w={ww} h={wh}
                isSelected={isWordSelected}
                onClick={(_sx, _sy) => {
                  if (annotId != null) {
                    select(annotId);
                  } else if (isWordSelected) {
                    selectWord(null);
                    const stageRect = stageRef.current?.container().getBoundingClientRect();
                    const wordScreenX = stageRect ? stageRect.left + wx : _sx;
                    const wordScreenY = stageRect ? stageRect.top + wy + wh + 8 : _sy;
                    onWordDoubleClick?.({
                      pageNumber,
                      xNorm: w.x_norm, yNorm: w.y_norm,
                      widthNorm: w.width_norm, heightNorm: w.height_norm,
                      screenX: wordScreenX, screenY: wordScreenY,
                    });
                  } else {
                    selectWord(wordKey);
                  }
                }}
                onDblClick={(sx, sy) => {
                  if (annotId != null) select(annotId);
                  else onWordDoubleClick?.({
                    pageNumber,
                    xNorm: w.x_norm, yNorm: w.y_norm,
                    widthNorm: w.width_norm, heightNorm: w.height_norm,
                    screenX: sx, screenY: sy,
                  });
                }}
              />
            );
          })}

          {/* Dev: OCR confidence labels — top-right corner of each word box */}
          {showOcrConfidence && pageWords.map((w, i) => {
            if (w.confidence == null) return null;
            const wx = w.x_norm * widthPx;
            const wy = w.y_norm * heightPx;
            const ww = w.width_norm * widthPx;
            const confPx = Math.max(6, Math.round(6.5 * zoom));
            const pct = Math.round(w.confidence * 100);
            const fill = w.confidence >= ocrThreshold ? "rgba(0,160,0,0.85)" : "rgba(200,0,0,0.85)";
            return (
              <Text
                key={`conf-${i}`}
                x={wx + ww - confPx * 2.2}
                y={wy + 1}
                text={String(pct)}
                fontSize={confPx}
                fontFamily="monospace"
                fill={fill}
                listening={false}
              />
            );
          })}

          {/* Inline glyphs — underlines, boxes, icons. A group-highlighted flag
              (raster criterion/task expansion) is framed like a selected one. */}
          {annotations.map((a) => (
            <InlineGlyph
              key={a.id}
              annotation={a}
              widthPx={widthPx}
              heightPx={heightPx}
              fontPx={fontPx}
              isSelected={selectedId === a.id || highlightIds.has(a.id)}
              onClick={() => select(a.id)}
            />
          ))}

          {/* Green frame around a clicked task's answer region (docs/05 §5). */}
          {frameRegion && frameRegion.page === pageNumber && (
            <Rect
              x={frameRegion.bbox[0] * widthPx}
              y={frameRegion.bbox[1] * heightPx}
              width={(frameRegion.bbox[2] - frameRegion.bbox[0]) * widthPx}
              height={(frameRegion.bbox[3] - frameRegion.bbox[1]) * heightPx}
              stroke="#2ea043"
              strokeWidth={2.5}
              cornerRadius={4}
              listening={false}
            />
          )}

          {/* Stacked margin content — language/note corrections + content [n] markers */}
          {annotations.map((a) => {
            const slot = marginSlots.get(a.id);
            if (!slot) return null;
            const x = slot.col === 0 ? col0X : col1X;
            const y = slot.y - fontPx * 0.3;
            const isMargSel = selectedId === a.id;

            if (a.type === "content") {
              const fn = footnoteNums.get(a.id);
              if (!fn) return null;
              return (
                <Text
                  key={`m-${a.id}`}
                  x={x} y={y}
                  text={`[${fn}]`}
                  fill={isMargSel ? ACCENT_SEL : ACCENT}
                  fontFamily="Caveat, cursive"
                  fontSize={fontPx * 0.85}
                  width={colWidth}
                  listening preventDefault={false}
                  onClick={() => select(a.id)}
                  onTap={() => select(a.id)}
                />
              );
            }

            const text = marginText(a);
            if (!text) return null;
            const color = isMargSel ? ACCENT_SEL : (a.status === "dismissed" ? MUTED : ACCENT);
            return (
              <Text
                key={`m-${a.id}`}
                x={x} y={y}
                text={text + ptSuffix(a.point_deduction)}
                fill={color}
                fontFamily="Caveat, cursive"
                fontSize={fontPx}
                width={colWidth}
                wrap="word"
                listening preventDefault={false}
                onClick={() => select(a.id)}
                onTap={() => select(a.id)}
              />
            );
          })}

          {/* Footnote area for content annotations */}
          {footnotes.length > 0 && (() => {
            const fnFontPx = fontPx * 0.9;
            const cpl = Math.max(10, Math.floor(fnColWidth / (fnFontPx * 0.58)));
            const elements = [];
            elements.push(
              <Rect
                key="fn-rule"
                x={0} y={heightPx + 6}
                width={widthPx} height={0.5}
                fill="rgba(0,0,0,0.18)" listening={false}
              />,
            );
            let yOff = heightPx + 14;
            const FN_INDENT = 8;
            for (const fn of footnotes) {
              const lineText = `[${fn.num}] ${fn.text}${ptSuffix(fn.deduction)}`;
              elements.push(
                <Text
                  key={`fn-${fn.id}`}
                  x={FN_INDENT} y={yOff}
                  text={lineText}
                  fill={selectedId === fn.id ? ACCENT_SEL : ACCENT}
                  fontFamily="Caveat, cursive"
                  fontSize={fnFontPx}
                  width={fnColWidth - FN_INDENT}
                  wrap="word"
                  listening preventDefault={false}
                  onClick={() => select(fn.id)}
                  onTap={() => select(fn.id)}
                />,
              );
              yOff += Math.max(1, Math.ceil(lineText.length / cpl)) * fnFontPx * 1.5 + 5;
            }
            return elements;
          })()}
        </Layer>
      </Stage>
    </div>
  );
}

// ── Word hit rect ─────────────────────────────────────────────────────────

interface WordHitRectProps {
  x: number; y: number; w: number; h: number;
  isSelected: boolean;
  onClick: (screenX: number, screenY: number) => void;
  onDblClick: (screenX: number, screenY: number) => void;
}

function WordHitRect({ x, y, w, h, isSelected, onClick, onDblClick }: WordHitRectProps) {
  return (
    <>
      <Rect
        x={x} y={y} width={w} height={h}
        fill="rgba(0,0,0,0.01)"
        listening
        preventDefault={false}
        onClick={(e) => onClick(e.evt.clientX, e.evt.clientY)}
        onTap={(e) => {
          const t = (e.evt as TouchEvent).changedTouches[0];
          onClick(t?.clientX ?? 0, t?.clientY ?? 0);
        }}
        onDblClick={(e) => onDblClick(e.evt.clientX, e.evt.clientY)}
        onDblTap={(e) => {
          const t = (e.evt as TouchEvent).changedTouches[0];
          onDblClick(t?.clientX ?? 0, t?.clientY ?? 0);
        }}
      />
      {isSelected && (
        <Rect
          x={x - 2} y={y - 1} width={w + 4} height={h + 2}
          stroke={WORD_SEL} strokeWidth={2.5} fill="transparent"
          listening={false}
        />
      )}
    </>
  );
}

// ── Inline glyph — highlight only, no margin text ─────────────────────────

interface InlineGlyphProps {
  annotation: Annotation;
  widthPx: number;
  heightPx: number;
  fontPx: number;
  isSelected: boolean;
  onClick: () => void;
}

function InlineGlyph({
  annotation: a,
  widthPx,
  heightPx: _heightPx,
  fontPx,
  isSelected,
  onClick,
}: InlineGlyphProps) {
  const x = a.x_norm * widthPx;
  const y = a.y_norm * _heightPx;
  const w = (a.width_norm ?? 0) * widthPx;
  const h = (a.height_norm ?? 0) * _heightPx;

  if (a.type === "ocr_flag") {
    if (a.status !== "pending") return null;
    return (
      <>
        <Rect
          x={x} y={y + h - 2} width={Math.max(w, 4)} height={1.5}
          fill={OCR_WARN}
          onClick={onClick} onTap={onClick} listening preventDefault={false}
        />
        {isSelected && (
          <Rect
            x={x - 2} y={y - 2} width={w + 4} height={h + 4}
            stroke={OCR_WARN} strokeWidth={2.5} fill="transparent" listening={false}
          />
        )}
      </>
    );
  }

  if (a.type === "language") {
    const color = a.status === "dismissed" ? MUTED : ACCENT;
    return (
      <>
        <Rect
          x={x} y={y + h - 2} width={Math.max(w, 4)}
          height={a.status === "accepted" || a.status === "corrected" ? 2 : 1.5}
          fill={color} onClick={onClick} onTap={onClick} listening preventDefault={false}
        />
        {isSelected && (
          <Rect
            x={x - 2} y={y - 2} width={w + 4} height={h + 4}
            stroke={ACCENT} strokeWidth={2.5} fill="transparent" listening={false}
          />
        )}
      </>
    );
  }

  if (a.type === "content") {
    return (
      <Rect
        x={x} y={y} width={Math.max(w, 8)} height={Math.max(h, 8)}
        fill="rgba(245,108,115,0.12)"
        stroke={isSelected ? ACCENT : "transparent"}
        strokeWidth={isSelected ? 2.5 : 0}
        onClick={onClick} onTap={onClick} listening preventDefault={false}
      />
    );
  }

  // note — ✎ icon at position
  return (
    <Text
      x={x} y={y}
      text="✎"
      fill={isSelected ? "#e55a60" : ACCENT}
      fontFamily="Caveat, cursive"
      fontSize={fontPx + 2}
      onClick={onClick} onTap={onClick} listening preventDefault={false}
    />
  );
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
